import { Anchor, Group, Pagination, SegmentedControl, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { stevilo } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { statusPovratne } from '../povratne/povratne';
import { Oznaka } from '../strani/PregledRacunov';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

const VELIKOST = 25;

const STANJA = [
  { value: 'odprte', label: 'Odprte' },
  { value: 'nove', label: 'Nove' },
  { value: 'ogledane', label: 'Ogledane' },
  { value: 'zakljucene', label: 'Zaključene' },
  { value: 'vse', label: 'Vse' },
];

/**
 * Skrbništvo → Povratne informacije: niti vseh instanc (privzeto odprte = nove in ogledane), najnovejša aktivnost
 * najprej, iskanje po instanci, avtorju in besedilu, strani na strežniku; stanje filtra v URL-ju, da ga Nazaj iz
 * pogovora ohrani. Klik odpre pogovor ({@link PovratnaSkrbnistvo}) z odgovorom in stanjem.
 */
export function PovratneSkrbnistvo() {
  const navigate = useNavigate();
  const [parametri, setParametri] = useSearchParams();
  const stanje = parametri.get('stanje') ?? 'odprte';
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje.trim(), 300);
  const [stran, setStran] = useState(0);
  const seznam = useQuery({
    queryKey: ['skrbnik', 'povratne', stanje, zakasnjeno, stran],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/povratne-informacije', {
            signal,
            headers: glave(),
            params: { query: { stanje, iskanje: zakasnjeno || undefined, stran, velikost: VELIKOST } },
          }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const skupaj = seznam.data?.skupaj ?? 0;
  const strani = Math.max(1, Math.ceil(skupaj / VELIKOST));
  const nastaviStanje = (v: string) => {
    const p = new URLSearchParams(window.location.search);
    if (v === 'odprte') {
      p.delete('stanje');
    } else {
      p.set('stanje', v);
    }
    setParametri(p, { replace: true });
    setStran(0);
  };

  return (
    <Kartica naslov="Povratne informacije strank">
      <Stack gap="sm">
        <Group gap="sm" wrap="wrap">
          <SegmentedControl aria-label="Stanje" data={STANJA} value={stanje} onChange={nastaviStanje} />
          <TextInput
            aria-label="Iskanje po povratnih informacijah"
            placeholder="Instanca, avtor ali besedilo"
            leftSection={<IconSearch size={16} />}
            value={iskanje}
            onChange={(e) => {
              setIskanje(e.currentTarget.value);
              setStran(0);
            }}
            w={{ base: '100%', sm: 320 }}
          />
        </Group>
        {!seznam.data ? (
          <StanjeNalaganja
            napaka={seznam.error}
            ponovi={() => void seznam.refetch()}
            naslov="Povratnih informacij ni bilo mogoče naložiti"
          />
        ) : seznam.data.vsebina?.length ? (
          <>
            <Table.ScrollContainer minWidth={900}>
              <Table highlightOnHover verticalSpacing={8}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Zadnje sporočilo</Table.Th>
                    <Table.Th>Instanca</Table.Th>
                    <Table.Th>Avtor</Table.Th>
                    <Table.Th>Sporočilo</Table.Th>
                    <Table.Th>Stanje</Table.Th>
                    <Table.Th>Odgovorjeno</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {seznam.data.vsebina.map((p) => {
                    const s = statusPovratne(p.status);
                    return (
                      <Table.Tr
                        key={p.id}
                        style={{ cursor: 'pointer', fontWeight: p.status === 'NOVO' ? 700 : undefined }}
                        onClick={() => void navigate(`/skrbnistvo/povratne-informacije/${p.id}`)}
                      >
                        <Table.Td className="brez-preloma">{cas(p.zadnjeSporocilo)}</Table.Td>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/skrbnistvo/${p.companyId}`}
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {p.instanca}
                          </Anchor>
                        </Table.Td>
                        <Table.Td className="brez-preloma">{p.avtor}</Table.Td>
                        <Table.Td maw={380}>
                          <Anchor
                            component={Link}
                            to={`/skrbnistvo/povratne-informacije/${p.id}`}
                            size="sm"
                            fw={p.status === 'NOVO' ? 700 : 500}
                            truncate
                            display="block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {p.povzetek}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
                        </Table.Td>
                        <Table.Td>
                          {p.odgovorjeno && (
                            <IconCheck size={16} aria-label="odgovorjeno" color="var(--mantine-color-green-7)" />
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {stevilo(stran * VELIKOST + 1)}–{stevilo(Math.min(skupaj, (stran + 1) * VELIKOST))} od {stevilo(skupaj)}
              </Text>
              {strani > 1 && (
                <Pagination total={strani} value={stran + 1} onChange={(v) => setStran(v - 1)} size="sm" />
              )}
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {zakasnjeno ? 'Ni povratnih informacij, ki bi ustrezale iskanju.' : 'Ni povratnih informacij v tem stanju.'}
          </Text>
        )}
      </Stack>
    </Kartica>
  );
}
